import { Stepper, useSteps } from "@exopengithub/exokit";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { integrationModalTitle } from "src/features/integrations/integrationModalTitle.js";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "src/common/utils/hooks/useDocumentTitle";
import { VismaNetDimensionSectionWithData } from "./VismaNetDimensionSection.js";

import { AddDataSourcePage } from "../../activate/AddDataSourcePage.js";
import { useSourceSystemMetadata } from "src/features/integrations/metadata.js";
import { AddDataSourceNavigation } from "../../activate/AddDataSourceNavigation.js";
import { AddDataSourceCompanyDomainSection } from "../../activate/AddDataSourceCompanyDomainSection.js";
import { useCreateCompanyMutation } from "../../../../common/redux/api/exopenApi.js";

import {
	getDefaultValuesForAddDataSource,
	type AddDataSourceInputs,
} from "../../activate/schema.js";
import { useLoadedCompanyDomains } from "src/routes/routeDataHooks.js";
import { useNavigate } from "react-router";
import { useCreateVismanetIntegrationMutation } from "src/common/service/fusionHub/integrations/vismanetApi.js";

interface VismaNetAddDataSourceInputs extends AddDataSourceInputs {
	tenantId: string;
	costCenterId: number | null;
	projectId: number | null;
}

const getDefaultValues = (
	operation: AddDataSourceInputs["operation"],
): VismaNetAddDataSourceInputs => {
	return {
		...getDefaultValuesForAddDataSource(operation),
		tenantId: "",
		costCenterId: null,
		projectId: null,
	};
};

export const AddVismaNetIntegrationRedirect = () => {
	const { t } = useTranslation();
	const { logoUrl } = useSourceSystemMetadata("VISMANET");
	useDocumentTitle(t("Add data source"));
	const navigate = useNavigate();
	const [createVismanetIntegration] = useCreateVismanetIntegrationMutation();
	const [createCompanyDomain] = useCreateCompanyMutation();

	const { activeStep, nextStep, prevStep } = useSteps({
		initialStep: 0,
	});
	const companyDomains = useLoadedCompanyDomains();
	const form = useForm({
		defaultValues: getDefaultValues(
			companyDomains.length === 0 ? "new" : "add",
		),
	});

	const submitHandler: SubmitHandler<VismaNetAddDataSourceInputs> = async (
		data,
	) => {
		if (data.operation === "add" && data.companyDomainId) {
			const result = await createVismanetIntegration({
				companyDomainId: data.companyDomainId,
				tenantId: data.tenantId,
				subAccountSegmentIds: {
					costCenterId: data.costCenterId ?? null,
					projectId: data.projectId ?? null,
				},
			});
			if ("error" in result) {
				showErrorNotification({ message: t("Failed to add data source") });
			} else {
				showSuccessNotification({
					message: t("The data source was saved"),
				});
				navigate(`/company-domains/${data.companyDomainId}/data-sources`);
			}
		}

		if (data.operation === "new") {
			const companyDomainResult = await createCompanyDomain({
				name: data.companyDomainName,
				type: "Kund",
			});

			if ("error" in companyDomainResult) {
				showErrorNotification({
					message: t("Something failed..."),
				});
			} else {
				const result = await createVismanetIntegration({
					companyDomainId: companyDomainResult.data.id?.toString(),
					tenantId: data.tenantId,
					subAccountSegmentIds: {
						costCenterId: data.costCenterId ?? null,
						projectId: data.projectId ?? null,
					},
				});

				if ("error" in result) {
					showErrorNotification({
						message: t("Something failed..."),
					});
				} else {
					navigate(
						`/company-domains/${companyDomainResult.data.id}/data-sources`,
					);
				}
			}
		}
	};

	return (
		<form onSubmit={form.handleSubmit(submitHandler)}>
			<AddDataSourcePage
				title={integrationModalTitle(t, "VismaNet")}
				logo={logoUrl}
			>
				<Stepper step={activeStep} size="sm" className="my-4">
					<Stepper.Step label={t("Tenant & dimensions")}>
						<AddDataSourceNavigation
							form={form}
							onNextStep={nextStep}
							disableOnNextStep={!form.watch("tenantId")}
						>
							<VismaNetDimensionSectionWithData form={form} />
						</AddDataSourceNavigation>
					</Stepper.Step>
					<Stepper.Step label={t("Select company domain")}>
						<AddDataSourceNavigation
							form={form}
							onBack={prevStep}
							onSubmit
							disableOnSubmit={
								!form.watch("companyDomainName") &&
								!form.watch("companyDomainId")
							}
						>
							<AddDataSourceCompanyDomainSection
								form={form}
								companyDomains={companyDomains}
							/>
						</AddDataSourceNavigation>
					</Stepper.Step>
				</Stepper>
			</AddDataSourcePage>
		</form>
	);
};
